import { forwardRef, Ref, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import styleVariables from '@/styles/style.module.scss';

export interface FakeCaretInputRef {
    focus: () => void;
    blur: () => void;
}

interface FakeCaretInputProps {
    value: string;
    disabled?: boolean;
    focused: boolean;
    setFocused: (value: boolean) => void;
    inputTestId?: string;
}

const CARET_GAP = 1;

const FakeCaretInput = forwardRef(
    (
        { value, disabled = false, focused, setFocused, inputTestId }: FakeCaretInputProps,
        ref: Ref<FakeCaretInputRef>,
    ) => {
        const containerRef = useRef<HTMLDivElement | null>(null);
        const fakeInputRef = useRef<HTMLDivElement | null>(null);
        const caretAnchorRef = useRef<HTMLSpanElement | null>(null);

        const [translateX, setTranslateX] = useState(0);
        const [caretLeft, setCaretLeft] = useState(0);

        const hasValue = value.length > 0;
        const showFakeCaret = focused && !disabled;

        const updateCaretPosition = useCallback(() => {
            const containerEl = containerRef.current;
            const anchorEl = caretAnchorRef.current;

            if (!containerEl || !anchorEl) return;

            if (!hasValue) {
                setTranslateX(0);
                setCaretLeft(0);
                return;
            }

            const containerWidth = containerEl.clientWidth;
            const anchorLeft = anchorEl.offsetLeft + CARET_GAP;

            let nextTranslateX = 0;

            if (anchorLeft > containerWidth - 1) {
                nextTranslateX = anchorLeft - (containerWidth - 1);
            }

            setTranslateX(nextTranslateX);
            setCaretLeft(anchorLeft - nextTranslateX);
        }, [hasValue]);

        useImperativeHandle(ref, () => ({
            focus: () => {
                if (disabled) return;
                fakeInputRef.current?.focus();
            },
            blur: () => {
                if (document.activeElement === fakeInputRef.current) {
                    fakeInputRef.current?.blur();
                }
            },
        }));

        useLayoutEffect(() => {
            updateCaretPosition();
        }, [value, focused, hasValue, updateCaretPosition]);

        useEffect(() => {
            const handleResize = () => {
                updateCaretPosition();
            };

            window.addEventListener('resize', handleResize);

            return () => {
                window.removeEventListener('resize', handleResize);
            };
        }, [hasValue, updateCaretPosition]);

        const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
            if (disabled) return;

            event.preventDefault();
            fakeInputRef.current?.focus();

            requestAnimationFrame(() => {
                updateCaretPosition();
            });
        };

        return (
            <Box
                ref={containerRef}
                sx={{
                    position: 'relative',
                    flex: 1,
                    minWidth: 0,
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    overflow: 'hidden',
                }}
            >
                <Box
                    ref={fakeInputRef}
                    data-testid={inputTestId}
                    tabIndex={disabled ? -1 : 0}
                    onFocus={() => {
                        if (disabled) return;
                        setFocused(true);

                        requestAnimationFrame(() => {
                            updateCaretPosition();
                        });
                    }}
                    onBlur={() => {
                        if (disabled) return;
                        setFocused(false);
                    }}
                    onPointerDown={handlePointerDown}
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        overflow: 'hidden',
                        outline: 'none',
                        cursor: disabled ? 'default' : 'text',
                        color: 'inherit',
                        backgroundColor: 'transparent',
                        whiteSpace: 'nowrap',
                    }}
                >
                    <Box
                        component="span"
                        sx={{
                            position: 'relative',
                            display: 'inline-flex',
                            alignItems: 'center',
                            minWidth: 0,
                            whiteSpace: 'nowrap',
                            transform: `translateX(-${translateX}px)`,
                            willChange: 'transform',
                        }}
                    >
                        <Box
                            component="span"
                            sx={{
                                whiteSpace: 'nowrap',
                                color: hasValue ? 'inherit' : styleVariables.darkGrey,
                            }}
                        >
                            {value}
                        </Box>

                        <Box
                            component="span"
                            ref={caretAnchorRef}
                            sx={{
                                display: 'inline-block',
                                width: '0px',
                                height: '1em',
                                verticalAlign: 'baseline',
                            }}
                        />
                    </Box>

                    {showFakeCaret && (
                        <Box
                            aria-hidden
                            sx={{
                                position: 'absolute',
                                left: `${caretLeft}px`,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                width: '1px',
                                height: '1.375em',
                                backgroundColor: 'black',
                                pointerEvents: 'none',
                                animation: 'scannerFakeCaretBlink 1s steps(1) infinite',
                                '@keyframes scannerFakeCaretBlink': {
                                    '0%, 49%': {
                                        opacity: 1,
                                    },
                                    '50%, 100%': {
                                        opacity: 0,
                                    },
                                },
                            }}
                        />
                    )}
                </Box>
            </Box>
        );
    },
);

export default FakeCaretInput;
